import { LISTING_STATUS } from '../../../../store/products/constants/listingStatus';
import {
  getListingProductStatusFromMap,
  hasListingProductInMap,
  hasLiveInProductListingsMap,
} from '../../../../store/selectors/productSelectors';
import SYNC_FLOW_MEDIA_STEPS from '../constants/syncFlowMediaSteps';
import {
  getProductMediaAssets,
  getVariantMediaAssets,
} from './syncFlowMediaHelpers';

export const CHANNEL_SYNC_ACTION = {
  DRAFT: 'draft',
  PUBLISH: 'publish',
};

export const SYNC_SETUP_MODE = {
  PUBLISH: 'publish',
  CREATE_DRAFTS: 'createDrafts',
};

export const MEDIA_SYNC_MODE = {
  ADD: 'add',
  REPLACE: 'replace',
};

export const isMediaInScope = (media) => (
  media === true
  || media === MEDIA_SYNC_MODE.ADD
  || media === MEDIA_SYNC_MODE.REPLACE
);

export const createInitialFlowState = ({
  productId,
  masterVariantIds,
  isVariantSync,
}) => ({
  productId,
  masterVariantIds,
  isVariantSync,
  selectedChannels: [],
  channelActionsByChannelId: {},
  overrideExisting: true,
  publishingScope: { text: true, media: false },
  productMediaByChannel: {},
  variantMediaByChannel: {},
  variantMediaStepCompleted: false,
  mediaAltTextByChannel: {},
});

export const hasListingForChannel = (productListings, channelId) => (
  hasListingProductInMap(productListings, channelId)
);

export const hasListingForAllChannels = (selectedChannels, productListings) => (
  selectedChannels.every((channelId) => hasListingForChannel(productListings, channelId))
);

export const shouldSkipMediaSteps = (scope) => !isMediaInScope(scope?.media);

export const getMediaSteps = ({
  product,
  isVariantSync,
  scope,
}) => {
  if (shouldSkipMediaSteps(scope)) {
    return [];
  }

  const steps = [];

  if (isVariantSync) {
    steps.push(SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA);
  } else {
    const hasVariants = Boolean(product?.variants?.length);

    if (hasVariants) {
      steps.push(SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA, SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA);
    } else {
      steps.push(SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA);
    }
  }

  steps.push(SYNC_FLOW_MEDIA_STEPS.ALT_CONFLICTS);
  return steps;
};

export const filterMediaStepsByAvailableAssets = ({
  steps,
  product,
  masterVariantIds,
  productsState,
  isVariantSync = false,
}) => steps.filter((step) => {
  if (step === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
    return getProductMediaAssets(product, productsState).length > 0;
  }

  if (step === SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA) {
    return getVariantMediaAssets(product, masterVariantIds, productsState).length > 0;
  }

  if (step === SYNC_FLOW_MEDIA_STEPS.ALT_CONFLICTS) {
    if (isVariantSync) {
      return getVariantMediaAssets(product, masterVariantIds, productsState).length > 0;
    }

    return getProductMediaAssets(product, productsState).length > 0
      || getVariantMediaAssets(product, masterVariantIds, productsState).length > 0;
  }

  return true;
});

export const hasAttachedMediaForSync = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => {
  const steps = filterMediaStepsByAvailableAssets({
    steps: getMediaSteps({ product, isVariantSync, scope: { text: true, media: true } }),
    product,
    masterVariantIds,
    productsState,
    isVariantSync,
  });

  return steps.length > 0;
};

export const isMediaScopeAvailable = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => (
  hasAttachedMediaForSync({ product, isVariantSync, masterVariantIds, productsState })
);

export const getDefaultPublishingScope = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => {
  const mediaAvailable = isMediaScopeAvailable({
    product,
    isVariantSync,
    masterVariantIds,
    productsState,
  });

  return {
    text: true,
    media: mediaAvailable ? MEDIA_SYNC_MODE.REPLACE : false,
  };
};

export const mergeFlowState = (flowState, patch) => ({
  ...flowState,
  ...patch,
});

export const getDefaultChannelAction = (channelId, productListings) => {
  if (hasLiveInProductListingsMap(productListings, channelId)) {
    return CHANNEL_SYNC_ACTION.PUBLISH;
  }

  if (getListingProductStatusFromMap(productListings, channelId) === LISTING_STATUS.DRAFT) {
    return CHANNEL_SYNC_ACTION.DRAFT;
  }

  return CHANNEL_SYNC_ACTION.DRAFT;
};

export const buildInitialChannelActions = (channelOptions, productListings) => (
  channelOptions.reduce((acc, { value: channelId }) => ({
    ...acc,
    [channelId]: getDefaultChannelAction(channelId, productListings),
  }), {})
);

export const splitChannelsByAction = (channelActionsByChannelId) => {
  const draftChannels = [];
  const publishChannels = [];

  Object.entries(channelActionsByChannelId).forEach(([channelId, action]) => {
    if (action === CHANNEL_SYNC_ACTION.PUBLISH) {
      publishChannels.push(channelId);
    } else {
      draftChannels.push(channelId);
    }
  });

  return { draftChannels, publishChannels };
};

export const resolveSyncChannelIds = ({
  selectedChannelIds,
  conflictingChannelIds,
  overrideExisting,
}) => {
  if (overrideExisting) {
    return selectedChannelIds;
  }

  return selectedChannelIds.filter((channelId) => !conflictingChannelIds.includes(channelId));
};
